// Shared compose component used by every message-typing surface
// (channel chat, thread panel, DM conversation, cc-chat via chat.ts).
//
// `mountMessageCompose(container, opts)` builds the entire compose DOM —
// reply bar + attachment preview + compose row with attach/textarea/GIF/
// sticker/emoji icons and a circular accent send button. No format
// toolbar — Discord doesn't have one (markdown parsing stays, buttons
// don't). Every chat surface calls this one helper instead of duplicating
// compose HTML. See also the helper functions below which are still
// consumed directly by surfaces that want to wire extra behaviour onto
// the textarea (typing indicator, mention/slash pickers, etc.).
//
// The handle returned exposes `focus()`, `destroy()`, `setReplyTo(msg)`,
// `clearReplyTo()`, plus direct refs to the `textarea` and `sendBtn` so
// the caller can layer its own listeners on top without duplicating DOM
// lookups.

type Cleanup = () => void;

const DRAFT_PREFIX = 'hub-people-draft:';
const DRAFT_DEBOUNCE_MS = 250;

type DraftHandle = {
  clear: () => void;
  unbind: Cleanup;
};

// Escape HTML — never trust caller-provided strings (reply chip snippets,
// author names, channel placeholders all flow through here).
function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

// Attribute-safe escape. `esc()` (textContent → innerHTML) escapes < > &
// but NOT " or ', so a placeholder containing a double-quote would
// break out of the attribute. escAttr handles those too.
function escAttr(s: string): string {
  return esc(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Auto-grow clamp. 200px (the previous hardcoded max) is cramped on tall
// screens and too tall on phones. Clamp at the smaller of (a) the textarea's
// scrollHeight and (b) ~50% of the viewport — never less than 120px so a
// single full row is always shown. This caps growth before it pushes the
// chat scroll under the chrome (Mitchell's complaint).
function autoGrowMax(): number {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 600;
  return Math.max(120, Math.round(vh * 0.5));
}

// Persist textarea contents to localStorage under a stable key so a page
// reload or accidental close doesn't lose an in-progress reply. The key is
// expected to be unique per target (e.g. `ch:${server}:${channel}` or
// `thread:${id}` or `dm:${peer}`).
export function initComposeDraft(key: string, textarea: HTMLTextAreaElement): DraftHandle {
  const storageKey = DRAFT_PREFIX + key;
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved && !textarea.value) {
      textarea.value = saved;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, autoGrowMax()) + 'px';
    }
  } catch { /* private mode / storage disabled */ }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  const onInput = (): void => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      try {
        const v = textarea.value;
        if (v) localStorage.setItem(storageKey, v);
        else localStorage.removeItem(storageKey);
      } catch { /* noop */ }
    }, DRAFT_DEBOUNCE_MS);
  };
  textarea.addEventListener('input', onInput);

  return {
    clear: (): void => {
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    },
    unbind: (): void => {
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      textarea.removeEventListener('input', onInput);
    },
  };
}

type SendVisibilityOpts = {
  // If true, keep the send button visible on mobile (≤768px) even when empty.
  // Desktop still hides the button on empty for Discord parity. Default true.
  alwaysOnMobile?: boolean;
};

// Toggle send button between "hidden because empty" and "shown because has
// content". On mobile we always show it (big circular accent button is the
// primary action). Desktop follows Discord: hidden when empty.
export function updateSendButtonVisibility(
  textarea: HTMLTextAreaElement,
  sendBtn: HTMLButtonElement,
  opts: SendVisibilityOpts = {},
): Cleanup {
  const alwaysOnMobile = opts.alwaysOnMobile ?? true;
  const mq = window.matchMedia('(max-width: 768px)');

  const apply = (): void => {
    const hasText = textarea.value.trim().length > 0;
    const isMobile = mq.matches;
    const shouldShow = hasText || (isMobile && alwaysOnMobile);
    sendBtn.classList.toggle('is-hidden', !shouldShow);
    sendBtn.dataset['filled'] = hasText ? '1' : '0';
    // Mobile keeps the button visible even when empty; explicitly disable
    // it so a tap doesn't fire a no-op send (the runSend gate already
    // refuses, but the disabled state gives proper visual + ARIA feedback
    // and lets CSS dim the button).
    sendBtn.disabled = !hasText;
    sendBtn.setAttribute('aria-disabled', String(!hasText));
  };

  textarea.addEventListener('input', apply);
  const mqListener = (): void => apply();
  // Safari < 14 fallback shim
  if (mq.addEventListener) mq.addEventListener('change', mqListener);
  else mq.addListener(mqListener);
  apply();

  return (): void => {
    textarea.removeEventListener('input', apply);
    if (mq.removeEventListener) mq.removeEventListener('change', mqListener);
    else mq.removeListener(mqListener);
  };
}

// Long-press a send button on mobile (touch devices only) to fire a
// "silent send" callback (no notifications sent). Short taps still click
// normally. Returns a cleanup fn.
export function attachLongPressSilent(
  sendBtn: HTMLButtonElement,
  onSilent: () => void,
  thresholdMs = 600,
): Cleanup {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let fired = false;
  const start = (): void => {
    fired = false;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { fired = true; onSilent(); }, thresholdMs);
  };
  const cancel = (e: Event): void => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (fired && e.cancelable) e.preventDefault();
  };
  sendBtn.addEventListener('touchstart', start, { passive: true });
  sendBtn.addEventListener('touchend', cancel);
  sendBtn.addEventListener('touchcancel', cancel);
  sendBtn.addEventListener('touchmove', () => {
    if (timer) { clearTimeout(timer); timer = null; }
  }, { passive: true });

  return (): void => {
    sendBtn.removeEventListener('touchstart', start);
    sendBtn.removeEventListener('touchend', cancel);
    sendBtn.removeEventListener('touchcancel', cancel);
    if (timer) clearTimeout(timer);
  };
}

// Focus helper — if draft was restored, place cursor at end so user can
// continue typing.
export function focusAtEnd(textarea: HTMLTextAreaElement): void {
  textarea.focus();
  const end = textarea.value.length;
  try { textarea.setSelectionRange(end, end); } catch { /* some browsers */ }
}

// ────────────────────────────────────────────────────────────────────────
// mountMessageCompose — builds the Discord-parity compose DOM.
//
// Layout (left to right):
//   [+ attach] [textarea ............................] [GIF] [Sticker]
//   [Emoji] [char-count] [▶ send]
//
// The component also renders (hidden by default) a reply chip row and an
// attachment-preview row above the compose. Both become visible when the
// caller invokes setReplyTo / addAttachment. No format toolbar — markdown
// still parses on the render side, but the B/I/S/<>/||/> buttons do not
// exist.
// ────────────────────────────────────────────────────────────────────────

export interface ReplyTarget {
  /** Message id used as reply_to on send. */
  id: string;
  /** Display name of the message author. */
  authorName: string;
  /** Role / profile colour for the author name, if any. */
  authorColor?: string;
  /** Short text snippet (≤120 chars recommended). */
  snippet: string;
  /** Whether the mention toggle is ON by default. Default true. */
  mentionOn?: boolean;
}

export interface SendOptions {
  /** Message id being replied to, if any. */
  reply_to?: string;
  /** Whether the replied-to author should be @mentioned. */
  mentionReplyAuthor?: boolean;
  /** Whether the caller long-pressed for silent-send (no notification). */
  silent?: boolean;
}

export interface MountComposeOpts {
  /** Unique key used for draft persistence (e.g. `ch:${sid}:${ch}`). */
  draftKey?: string;
  /** Identity markers per brief — not used internally, surface them for hooks. */
  channel_id?: string;
  dm_peer_id?: string;
  thread_id?: string;
  /** Placeholder text for the textarea. Rendered via textContent (safe). */
  placeholder: string;
  /** Maximum content length — defaults to 4000. */
  maxLength?: number;
  /** Called when the user triggers send (Enter or send-btn click). */
  onSend: (text: string, opts: SendOptions) => void;
  /**
   * Pre-send gate — return false to block. Default: permit when textarea
   * has non-whitespace content.
   */
  canSend?: (text: string) => boolean;
  /** Override the initial draft; skip localStorage lookup when provided. */
  initialDraft?: string;
  /** Whether to render the [+ attach] button. Default true. */
  showAttach?: boolean;
  /**
   * Whether to render the icon row (GIF / Sticker / Emoji). Default true.
   * Threads and DMs set this false to match their simpler compose.
   */
  showIconRow?: boolean;
  /** Whether Enter/Shift+Enter behaviour fires. Default true. */
  enableEnterToSend?: boolean;
  /** Whether reply-bar is enabled. Default true. */
  enableReplyBar?: boolean;
  /** Whether attachment preview row is rendered. Default: same as showAttach. */
  enableAttachments?: boolean;
  /** Called on long-press of send button (mobile). Default: no-op. */
  onSilentSend?: () => void;
  /** Called when textarea input fires — for typing indicators, etc. */
  onInput?: (textarea: HTMLTextAreaElement) => void;
  /** Called when a key is pressed — return true to consume before built-in handlers. */
  onKeydown?: (e: KeyboardEvent, textarea: HTMLTextAreaElement) => boolean | void;
  /** Called when files are added via the attach button / file input. */
  onFilesAdded?: (files: File[]) => void;
  /** Called when the reply chip is cleared (X or Esc). */
  onReplyCleared?: () => void;
}

export interface ComposeHandle {
  /** Tear down listeners and empty the container. */
  destroy: () => void;
  /** Focus the textarea, placing cursor at end. */
  focus: () => void;
  /** Show / update the reply chip. */
  setReplyTo: (reply: ReplyTarget) => void;
  /** Hide the reply chip. */
  clearReplyTo: () => void;
  /** Currently-tracked reply target (or null). */
  getReplyTo: () => ReplyTarget | null;
  /** Whether the reply-author mention toggle is ON (undefined if no reply). */
  isReplyMentionOn: () => boolean;
  /** Clear the persisted draft (after a successful send). */
  clearDraft: () => void;
  /** Reset textarea content + height. */
  resetInput: () => void;
  /** Textarea element for callers that want to layer autocomplete, etc. */
  textarea: HTMLTextAreaElement;
  /** Send button — exposed so callers can drive it from their own pickers. */
  sendBtn: HTMLButtonElement;
  /** Attach button (if rendered). */
  attachBtn: HTMLButtonElement | null;
  /** Emoji button (if rendered). */
  emojiBtn: HTMLButtonElement | null;
  /** GIF button (if rendered). */
  gifBtn: HTMLButtonElement | null;
  /** Sticker button (if rendered). */
  stickerBtn: HTMLButtonElement | null;
  /** File input — callers can read `.files` or call `.click()`. */
  fileInput: HTMLInputElement | null;
  /** Attachment preview container — callers own what they render inside. */
  attachPreview: HTMLElement | null;
}

/**
 * Build + wire the compose DOM inside `container`. Returns a handle for
 * the caller to drive + tear down. Every chat surface funnels through here.
 */
export function mountMessageCompose(container: HTMLElement, opts: MountComposeOpts): ComposeHandle {
  // 0 = no character limit (removed per Mitchell — re-add later by passing
  // opts.maxLength or restoring a default here). When 0 we omit the maxlength
  // attribute + the countdown counter entirely.
  const maxLen = opts.maxLength ?? 0;
  const showAttach = opts.showAttach ?? true;
  const showIconRow = opts.showIconRow ?? true;
  const enableEnter = opts.enableEnterToSend ?? true;
  const enableReplyBar = opts.enableReplyBar ?? true;
  const enableAttachments = opts.enableAttachments ?? showAttach;

  const replyBarHtml = enableReplyBar ? `
    <div class="chat-reply-bar" data-compose="reply" style="display:none">
      <div class="reply-bar-content">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="opacity:0.7;flex-shrink:0" aria-hidden="true"><path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/></svg>
        <span class="reply-bar-label">Replying to <strong data-compose="reply-name"></strong></span>
        <span class="reply-bar-snippet" data-compose="reply-snippet"></span>
      </div>
      <div class="reply-bar-actions">
        <button class="reply-bar-mention" type="button" data-compose="reply-mention" aria-pressed="true" title="Ping the replied-to author with @mention">@ ON</button>
        <button class="reply-bar-close" type="button" data-compose="reply-close" title="Cancel reply (Esc)" aria-label="Cancel reply">&times;</button>
      </div>
    </div>` : '';

  const attachPreviewHtml = enableAttachments ? `
    <div class="chat-attachments-preview" data-compose="attach-preview" style="display:none"></div>` : '';

  const attachBtnHtml = showAttach ? `
    <button class="chat-attach-btn" type="button" data-compose="attach-btn" title="Upload file" aria-label="Attach file">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style="opacity:0.5" aria-hidden="true"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>
    </button>
    <input type="file" data-compose="file-input" multiple style="display:none">` : '';

  // Mobile-only "+" expansion button (Discord parity). Collapses GIF /
  // sticker / emoji / attach actions into a single bottom-sheet so the
  // textarea gets the full compose row width on phones (Mitchell's
  // long-standing complaint: icons on the right block the input on mobile).
  // Hidden on desktop via CSS @media (display:none above 768px).
  const mobilePlusBtnHtml = `
    <button class="chat-mobile-plus" type="button" data-compose="mobile-plus" title="More" aria-label="More compose actions">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
    </button>`;

  const iconRowHtml = showIconRow ? `
    <div class="chat-compose-actions">
      <button class="chat-compose-btn" type="button" data-compose="gif-btn" title="GIF — coming soon" aria-label="Send a GIF">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><text x="12" y="15" text-anchor="middle" font-size="7" font-weight="700" fill="currentColor" stroke="none">GIF</text></svg>
      </button>
      <button class="chat-compose-btn" type="button" data-compose="sticker-btn" title="Sticker — coming soon" aria-label="Send a sticker">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v1a7 7 0 0 1-7 7h-1l-8-8V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7z"/><path d="M12 20a7 7 0 0 0 7-7"/></svg>
      </button>
      <button class="chat-compose-btn" type="button" data-compose="emoji-btn" title="Emoji" aria-label="Open emoji picker">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
      </button>
    </div>` : '';

  // Markdown formatting toolbar (mobile only via CSS @media). Tap to wrap
  // the current selection (or insertion point if no selection) with the
  // matching markdown sentinel: B = **, I = *, S = ~~, code = `, spoiler = ||.
  // Inline so it ships in the same compose render \u2014 no extra component file.
  const mdToolbarHtml = `
    <div class="chat-md-toolbar" role="toolbar" aria-label="Markdown formatting">
      <button class="chat-md-btn" type="button" data-md="b" title="Bold (Ctrl+B)" aria-label="Bold">B</button>
      <button class="chat-md-btn" type="button" data-md="i" title="Italic (Ctrl+I)" aria-label="Italic">I</button>
      <button class="chat-md-btn" type="button" data-md="s" title="Strikethrough" aria-label="Strikethrough">S</button>
      <button class="chat-md-btn" type="button" data-md="code" title="Inline code" aria-label="Code">{ }</button>
      <button class="chat-md-btn" type="button" data-md="spoiler" title="Spoiler" aria-label="Spoiler">||</button>
      <button class="chat-md-btn" type="button" data-md="quote" title="Quote" aria-label="Quote">&gt;</button>
    </div>
  `;

  container.innerHTML = `
    ${replyBarHtml}
    ${attachPreviewHtml}
    ${mdToolbarHtml}
    <div class="chat-compose">
      ${mobilePlusBtnHtml}
      <span class="chat-attach-wrap-desktop">${attachBtnHtml}</span>
      <textarea class="chat-input" data-compose="textarea"
        placeholder="${escAttr(opts.placeholder)}"
        ${maxLen > 0 ? `maxlength="${maxLen}"` : ''} rows="1" autocomplete="off"></textarea>
      <span class="chat-icons-wrap-desktop">${iconRowHtml}</span>
      <span class="chat-char-count" data-compose="char-count" aria-live="polite"></span>
      <button class="chat-send-btn" type="button" data-compose="send-btn" title="Send message" aria-label="Send message">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
      </button>
    </div>
  `;

  const textarea = container.querySelector<HTMLTextAreaElement>('[data-compose="textarea"]')!;
  const sendBtn = container.querySelector<HTMLButtonElement>('[data-compose="send-btn"]')!;
  const attachBtn = container.querySelector<HTMLButtonElement>('[data-compose="attach-btn"]');
  const fileInput = container.querySelector<HTMLInputElement>('[data-compose="file-input"]');
  const attachPreview = container.querySelector<HTMLElement>('[data-compose="attach-preview"]');
  const emojiBtn = container.querySelector<HTMLButtonElement>('[data-compose="emoji-btn"]');
  const gifBtn = container.querySelector<HTMLButtonElement>('[data-compose="gif-btn"]');
  const stickerBtn = container.querySelector<HTMLButtonElement>('[data-compose="sticker-btn"]');
  const mobilePlusBtn = container.querySelector<HTMLButtonElement>('[data-compose="mobile-plus"]');

  // Discord mobile pattern: tap "+" \u2192 bottom-sheet with the same actions
  // (attach, GIF, sticker, emoji) so the textarea has the full row width
  // when typing on a phone. The same buttons stay accessible on desktop in
  // the icon row to the right of the textarea.
  mobilePlusBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.querySelectorAll('.chat-mobile-plus-sheet, .chat-mobile-plus-backdrop').forEach(el => el.remove());
    // Dismiss the soft keyboard so the sheet isn't clipped by its overlay.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    const backdrop = document.createElement('div');
    backdrop.className = 'chat-mobile-plus-backdrop';
    document.body.appendChild(backdrop);
    const sheet = document.createElement('div');
    sheet.className = 'chat-mobile-plus-sheet';
    sheet.setAttribute('role', 'menu');
    sheet.setAttribute('aria-label', 'Compose actions');
    sheet.innerHTML = `
      <div class="cmps-grab" aria-hidden="true"></div>
      <div class="cmps-title">Compose actions</div>
      <button type="button" class="cmps-row" data-cmps="attach">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>
        Upload a File
      </button>
      <button type="button" class="cmps-row" data-cmps="gif">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><text x="12" y="15" text-anchor="middle" font-size="7" font-weight="700" fill="currentColor" stroke="none">GIF</text></svg>
        Send a GIF
      </button>
      <button type="button" class="cmps-row" data-cmps="sticker">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20 12v1a7 7 0 0 1-7 7h-1l-8-8V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7z"/><path d="M12 20a7 7 0 0 0 7-7"/></svg>
        Pick a Sticker
      </button>
      <button type="button" class="cmps-row" data-cmps="emoji">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
        Open Emoji Picker
      </button>
    `;
    document.body.appendChild(sheet);
    const close = (): void => {
      backdrop.removeEventListener('click', close);
      document.removeEventListener('keydown', onEsc);
      sheet.remove();
      backdrop.remove();
    };
    const onEsc = (ev: KeyboardEvent): void => { if (ev.key === 'Escape') close(); };
    document.addEventListener('keydown', onEsc);
    backdrop.addEventListener('click', close);
    // Swipe-down to dismiss (Discord mobile pattern). Drag tracks translateY
    // for visual feedback; release past threshold = close, else spring back.
    let startY = 0;
    let lastY = 0;
    let dragging = false;
    const onTouchStart = (ev: TouchEvent): void => {
      if (ev.touches.length !== 1) return;
      const t = ev.touches[0];
      if (!t) return;
      startY = t.clientY;
      lastY = startY;
      dragging = true;
      sheet.style.transition = 'none';
    };
    const onTouchMove = (ev: TouchEvent): void => {
      if (!dragging || ev.touches.length !== 1) return;
      const t = ev.touches[0];
      if (!t) return;
      lastY = t.clientY;
      const dy = Math.max(0, lastY - startY);
      sheet.style.transform = `translateY(${dy}px)`;
      backdrop.style.opacity = String(Math.max(0, 1 - dy / 400));
    };
    const onTouchEnd = (): void => {
      if (!dragging) return;
      dragging = false;
      const dy = Math.max(0, lastY - startY);
      sheet.style.transition = 'transform 160ms cubic-bezier(.2,.8,.2,1)';
      if (dy > 80) {
        sheet.style.transform = 'translateY(110%)';
        setTimeout(close, 160);
      } else {
        sheet.style.transform = 'translateY(0)';
        backdrop.style.opacity = '';
      }
    };
    sheet.addEventListener('touchstart', onTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onTouchMove, { passive: true });
    sheet.addEventListener('touchend', onTouchEnd);
    sheet.addEventListener('touchcancel', onTouchEnd);
    sheet.querySelectorAll<HTMLButtonElement>('.cmps-row').forEach(row => {
      row.addEventListener('click', () => {
        const which = row.dataset['cmps'];
        close();
        if (which === 'attach') attachBtn?.click();
        else if (which === 'gif') gifBtn?.click();
        else if (which === 'sticker') stickerBtn?.click();
        else if (which === 'emoji') emojiBtn?.click();
      });
    });
  });
  const charCountEl = container.querySelector<HTMLElement>('[data-compose="char-count"]');
  const replyBar = container.querySelector<HTMLElement>('[data-compose="reply"]');
  const replyName = container.querySelector<HTMLElement>('[data-compose="reply-name"]');
  const replySnippet = container.querySelector<HTMLElement>('[data-compose="reply-snippet"]');
  const replyMentionBtn = container.querySelector<HTMLButtonElement>('[data-compose="reply-mention"]');
  const replyCloseBtn = container.querySelector<HTMLButtonElement>('[data-compose="reply-close"]');

  // Per-compose state lives in closure so multiple mounted composes
  // (e.g. channel chat + thread panel open at the same time) don't stomp
  // each other.
  let replyTarget: ReplyTarget | null = null;
  let replyMentionOn = true;

  const cleanups: Cleanup[] = [];

  // Draft persistence — caller opts.initialDraft wins, else localStorage.
  let draftHandle: DraftHandle | null = null;
  if (opts.initialDraft !== undefined) {
    textarea.value = opts.initialDraft;
  }
  if (opts.draftKey) {
    draftHandle = initComposeDraft(opts.draftKey, textarea);
    cleanups.push(() => draftHandle?.unbind());
  }

  // Send-button visibility follows content + mobile media query.
  const visCleanup = updateSendButtonVisibility(textarea, sendBtn);
  cleanups.push(visCleanup);

  // Long-press silent send (mobile). If caller didn't provide a handler,
  // the long-press still fires but no-ops — the timer is still attached so
  // short-tap behaviour is not affected.
  const lpCleanup = attachLongPressSilent(sendBtn, () => {
    opts.onSilentSend?.();
  });
  cleanups.push(lpCleanup);

  const runSend = (silent = false): void => {
    const raw = textarea.value;
    const trimmed = raw.trim();
    const gate = opts.canSend ?? ((t: string) => t.length > 0);
    if (!gate(trimmed)) return;
    const sendOpts: SendOptions = {};
    if (replyTarget) {
      sendOpts.reply_to = replyTarget.id;
      sendOpts.mentionReplyAuthor = replyMentionOn;
    }
    if (silent) sendOpts.silent = true;
    opts.onSend(raw, sendOpts);
  };

  // Auto-resize + char counter + caller's onInput hook.
  const onInput = (): void => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, autoGrowMax()) + 'px';
    if (charCountEl && maxLen > 0) {
      const len = textarea.value.length;
      const threshold = Math.floor(maxLen * 0.5);
      if (len >= threshold) {
        charCountEl.textContent = `${maxLen - len}`;
        charCountEl.style.display = 'block';
        charCountEl.style.color = len >= maxLen - 100 ? 'var(--err)' : len >= maxLen - 500 ? 'var(--warning)' : 'var(--fg-muted)';
      } else {
        charCountEl.style.display = 'none';
      }
    } else if (charCountEl) {
      charCountEl.style.display = 'none';
    }
    opts.onInput?.(textarea);
  };
  textarea.addEventListener('input', onInput);
  cleanups.push(() => textarea.removeEventListener('input', onInput));

  // Keydown: caller hook runs first (so mention/slash/channel autocompletes
  // can intercept Enter/Tab/Arrow before we interpret them), then our own
  // default behaviour. Escape clears the reply bar.
  const onKeydown = (e: KeyboardEvent): void => {
    if (opts.onKeydown && opts.onKeydown(e, textarea)) return;
    if (e.key === 'Escape' && replyTarget) {
      clearReply();
      textarea.focus();
      return;
    }
    if (enableEnter && e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      runSend();
    }
  };
  textarea.addEventListener('keydown', onKeydown);
  cleanups.push(() => textarea.removeEventListener('keydown', onKeydown));

  // Markdown toolbar wiring \u2014 wraps the current selection (or insertion
  // point) with the matching sentinel. Re-uses textarea selection so a tap
  // on B with text selected wraps that text in **; with no selection it
  // inserts the sentinels and places the cursor between them. Quote prepends
  // "> " to the start of the current line.
  const MD_WRAPS: Record<string, [string, string]> = {
    b: ['**', '**'], i: ['*', '*'], s: ['~~', '~~'],
    code: ['`', '`'], spoiler: ['||', '||'],
  };
  container.querySelectorAll<HTMLButtonElement>('.chat-md-btn').forEach(btn => {
    const onClick = (e: Event): void => {
      e.preventDefault();
      const kind = btn.dataset['md'] ?? '';
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      if (kind === 'quote') {
        // Prepend "> " to the start of the current line
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const before = value.slice(0, lineStart);
        const after = value.slice(lineStart);
        textarea.value = before + '> ' + after;
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      } else {
        const wrap = MD_WRAPS[kind];
        if (!wrap) return;
        const [open, close] = wrap;
        const sel = value.slice(start, end);
        const inserted = `${open}${sel}${close}`;
        textarea.value = value.slice(0, start) + inserted + value.slice(end);
        if (sel) {
          textarea.selectionStart = start + open.length;
          textarea.selectionEnd = end + open.length;
        } else {
          textarea.selectionStart = textarea.selectionEnd = start + open.length;
        }
      }
      textarea.focus();
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    };
    btn.addEventListener('click', onClick);
    cleanups.push(() => btn.removeEventListener('click', onClick));
  });

  // Send button click.
  const onSendClick = (): void => runSend();
  sendBtn.addEventListener('click', onSendClick);
  cleanups.push(() => sendBtn.removeEventListener('click', onSendClick));

  // Attach button → file input.
  if (attachBtn && fileInput) {
    const onAttachClick = (): void => fileInput.click();
    attachBtn.addEventListener('click', onAttachClick);
    cleanups.push(() => attachBtn.removeEventListener('click', onAttachClick));

    const onFileChange = (): void => {
      if (!fileInput.files || fileInput.files.length === 0) return;
      const files = Array.from(fileInput.files);
      opts.onFilesAdded?.(files);
      fileInput.value = '';
    };
    fileInput.addEventListener('change', onFileChange);
    cleanups.push(() => fileInput.removeEventListener('change', onFileChange));
  }

  // Paste + drag-drop file ingestion. Discord parity: paste a screenshot or
  // drag a file onto the compose row → it routes through the same
  // onFilesAdded callback the attach button uses, so the caller's preview /
  // upload pipeline doesn't need a separate path. We only intercept when
  // the clipboard / drop actually carries files (text paste stays untouched
  // and falls through to the textarea's default behaviour).
  if (enableAttachments && opts.onFilesAdded) {
    const onPaste = (e: ClipboardEvent): void => {
      const dt = e.clipboardData;
      if (!dt) return;
      const files = Array.from(dt.files);
      if (files.length === 0) return;
      e.preventDefault();
      opts.onFilesAdded?.(files);
    };
    textarea.addEventListener('paste', onPaste);
    cleanups.push(() => textarea.removeEventListener('paste', onPaste));

    const dropZone = container.querySelector<HTMLElement>('.chat-compose') ?? container;
    const onDragOver = (e: DragEvent): void => {
      if (!e.dataTransfer || e.dataTransfer.types.indexOf('Files') === -1) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      dropZone.classList.add('chat-compose-drop-active');
    };
    const onDragLeave = (e: DragEvent): void => {
      // Only clear when leaving the drop zone entirely (not a child element).
      if (e.target === dropZone) dropZone.classList.remove('chat-compose-drop-active');
    };
    const onDrop = (e: DragEvent): void => {
      dropZone.classList.remove('chat-compose-drop-active');
      const files = e.dataTransfer ? Array.from(e.dataTransfer.files) : [];
      if (files.length === 0) return;
      e.preventDefault();
      opts.onFilesAdded?.(files);
    };
    dropZone.addEventListener('dragover', onDragOver);
    dropZone.addEventListener('dragleave', onDragLeave);
    dropZone.addEventListener('drop', onDrop);
    cleanups.push(() => {
      dropZone.removeEventListener('dragover', onDragOver);
      dropZone.removeEventListener('dragleave', onDragLeave);
      dropZone.removeEventListener('drop', onDrop);
    });
  }

  // Reply bar — close button + mention toggle.
  const clearReply = (): void => {
    replyTarget = null;
    replyMentionOn = true;
    if (replyBar) replyBar.style.display = 'none';
    opts.onReplyCleared?.();
  };

  if (replyCloseBtn) {
    const onClose = (): void => { clearReply(); textarea.focus(); };
    replyCloseBtn.addEventListener('click', onClose);
    cleanups.push(() => replyCloseBtn.removeEventListener('click', onClose));
  }
  if (replyMentionBtn) {
    const onMentionToggle = (e: MouseEvent): void => {
      e.preventDefault();
      replyMentionOn = !replyMentionOn;
      replyMentionBtn.textContent = replyMentionOn ? '@ ON' : '@ OFF';
      replyMentionBtn.classList.toggle('reply-bar-mention-on', replyMentionOn);
      replyMentionBtn.setAttribute('aria-pressed', String(replyMentionOn));
      textarea.focus();
    };
    replyMentionBtn.addEventListener('click', onMentionToggle);
    cleanups.push(() => replyMentionBtn.removeEventListener('click', onMentionToggle));
  }

  // Fire one input event so the draft-restore height adjustment + counter
  // state come up correct when remounting into an existing draft.
  if (textarea.value) {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, autoGrowMax()) + 'px';
  }

  const handle: ComposeHandle = {
    destroy: (): void => {
      for (const c of cleanups.splice(0, cleanups.length)) {
        try { c(); } catch { /* never let one cleanup sink another */ }
      }
      container.innerHTML = '';
    },
    focus: (): void => focusAtEnd(textarea),
    setReplyTo: (reply: ReplyTarget): void => {
      replyTarget = reply;
      replyMentionOn = reply.mentionOn ?? true;
      if (replyBar) replyBar.style.display = 'flex';
      if (replyName) {
        // textContent is safe — never use innerHTML for user-supplied text.
        replyName.textContent = reply.authorName;
        if (reply.authorColor) replyName.style.color = reply.authorColor;
        else replyName.style.removeProperty('color');
      }
      if (replySnippet) {
        const raw = (reply.snippet || '').trim();
        replySnippet.textContent = raw.length > 120 ? raw.substring(0, 120) + '…' : (raw || '(attachment)');
      }
      if (replyMentionBtn) {
        replyMentionBtn.textContent = replyMentionOn ? '@ ON' : '@ OFF';
        replyMentionBtn.classList.toggle('reply-bar-mention-on', replyMentionOn);
        replyMentionBtn.setAttribute('aria-pressed', String(replyMentionOn));
      }
    },
    clearReplyTo: clearReply,
    getReplyTo: (): ReplyTarget | null => replyTarget,
    isReplyMentionOn: (): boolean => replyMentionOn,
    clearDraft: (): void => draftHandle?.clear(),
    resetInput: (): void => {
      textarea.value = '';
      textarea.style.height = 'auto';
      draftHandle?.clear();
      if (charCountEl) {
        charCountEl.style.display = 'none';
        charCountEl.textContent = '';
      }
      // Fire input so send-button visibility recomputes.
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    },
    textarea,
    sendBtn,
    attachBtn,
    emojiBtn,
    gifBtn,
    stickerBtn,
    fileInput,
    attachPreview,
  };

  return handle;
}
